import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Card, Form, Spinner } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import { ColContainer, CTAButtons, PageHeader } from 'components/Page';
import { entityMapper } from 'rdx/hardware/installer/types';
import {
  getInstalledMSes,
  getAvailableMSes,
  getInstallerExists,
  getInstallerIsLoading,
} from 'rdx/hardware/installer';
import { getCurrentUserIsAdmin } from 'rdx/users';
import { addError } from 'rdx/alert';

import Manager from './Manager';

function MsConfigSelect() {
  const [microService, setMicroService] = useState('');
  const currentMSes = useSelector(getInstalledMSes);
  const availableMSes = useSelector(getAvailableMSes);
  const installerIsInstalled = useSelector(getInstallerExists);
  const isLoading = useSelector(getInstallerIsLoading);
  const dispatch = useDispatch();

  const isAdmin = useSelector(getCurrentUserIsAdmin);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isAdmin) {
      dispatch(
        addError(
          'Current user does not have rights to open micro-service manager.',
        ),
      );
      navigate(-1, { state: { from: location } });
    }
  }, [isAdmin, navigate, dispatch, location]);

  const handleSelect = (e) => {
    setMicroService(e.target.value);
  };

  const installedMS = [...new Set(currentMSes.map((ms) => ms.name))].map(
    (ms) =>
      ms && {
        value: ms,
        name: entityMapper[ms]?.name || ms,
      },
  );

  const newMS = availableMSes
    .filter((ms) => !currentMSes.map((ms2) => ms2.name).includes(ms))
    .map((ms) => ({
      value: ms,
      name: entityMapper[ms]?.name || ms,
    }));

  const buttons = {
    next: {
      disabled: !microService,
      text: 'Continue',
      onClick: () => navigate(`/hardware/installer/msconfig/${microService}`),
    },
  };

  if (isLoading)
    return (
      <div className="d-flex flex-column flex-fill justify-content-center align-items-center">
        <Spinner animation="border" />
      </div>
    );

  return (
    <div className="d-flex flex-fill flex-column">
      <PageHeader
        goBack={() => navigate('/hardware/installer')}
        title="(debug) Micro-service Manager"
      />
      <Alert variant="warning">
        This section is a direct access to the microservice files on{' '}
        <b>the current device</b>. Changes made here will not be reflected on
        other devices.
      </Alert>
      <Alert variant="danger">
        <p>
          Changes you make here are temporary and may get automatically
          overwritten by existing configuration.
        </p>
      </Alert>
      {installerIsInstalled ? (
        <Card className="d-flex flex-column integration-form">
          <Card.Body>
            <ColContainer
              col1={
                <Form.Group>
                  <Form.Label>Add new micro-service</Form.Label>
                  <Form.Control
                    as="select"
                    value={microService}
                    onChange={handleSelect}
                  >
                    <option>Select Micro-service</option>
                    {newMS.map(({ name, value }) => (
                      <option key={value} value={value}>
                        {name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              }
              col2={
                <Form.Group>
                  <Form.Label>Update existing micro-service</Form.Label>
                  <Form.Control
                    as="select"
                    value={microService}
                    onChange={handleSelect}
                  >
                    <option>Select Micro-service</option>
                    {installedMS.map(({ name, value }) => (
                      <option key={value} value={value}>
                        {name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              }
            />
          </Card.Body>
        </Card>
      ) : (
        <Alert variant="danger">
          Installer is not installed or unable to connect. Fix the issue{' '}
          <Link to="/hardware/installer/installation">here</Link>.
        </Alert>
      )}
      <CTAButtons buttons={buttons} />
    </div>
  );
}

export default {
  linkName: '(debug) Micro-service Manager',
  path: '/hardware/installer/msconfig',
  component: MsConfigSelect,
  subRoutes: [
    {
      hidden: true,
      path: '/hardware/installer/msconfig/:microService',
      component: Manager,
      rootPath: { name: 'Hardware', uri: '/hardware' },
      subPath: [
        { name: 'Installer', uri: '/hardware/installer' },
        { name: 'Micro-service Manager', uri: '/hardware/installer/msconfig' },
        { name: 'Micro-service Configuration' },
      ],
    },
  ],
  rootPath: { name: 'Hardware', uri: '/hardware' },
  subPath: [
    { name: 'Installer', uri: '/hardware/installer' },
    { name: 'Micro-service Manager' },
  ],
};
